import { useLocation, useNavigate } from "react-router-dom";
import { resetPassword } from "../../services/Api/loginService";
import Eye from "../../assets/icons/eye.svg";
import { useConnexionStore } from "../../stores/Layout/connexion";
import { showToastError, showToastSuccess } from "../../services/Toasts/toasts";
import { ToastContainer } from "react-toastify";
import logo from "../../assets/logos/logoX.png";

const ResetPassword = () => {
  // Hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Store
  const {
    passwordResetPassword,
    setPasswordResetPassword,
    verifyPasswordResetPassword,
    setVerifyPasswordResetPassword,
    passwordInputType,
    setPasswordInputType,
  } = useConnexionStore();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  // function for password visibility
  const togglePasswordVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setPasswordInputType(
      passwordInputType === "password" ? "text" : "password"
    );
  };

  const handlePassword = (e: any) => {
    e.preventDefault();
    if (passwordResetPassword !== verifyPasswordResetPassword) {
      showToastError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (passwordResetPassword.length < 8) {
      showToastError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    } else {
      resetPassword(token, verifyPasswordResetPassword)
        .then((response) => {
          if (response?.status === 200) {
            showToastSuccess("Le mot de passe a bien été réinitialisé !");
            setPasswordInputType("password");
            setTimeout(() => {
              navigate("/");
            }, 1.8);
          }
        })
        .catch(() => showToastError("Une erreur s'est produite"));
    }
  };

  return (
    <div className="relative min-h-dvh w-full overflow-x-hidden overflow-y-auto">
      <ToastContainer position="top-center" />
      
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100"></div>
      
      {/* Logo */}
      <div className="absolute left-6 top-5 z-30 hidden transition-transform duration-300 hover:scale-110 md:block lg:left-8 lg:top-6">
        <img src={logo} alt="logo Nexum" className="w-12 h-16 drop-shadow-lg" />
      </div>
      
      {/* Main container */}
      <main className="relative z-20 flex min-h-dvh w-full items-start justify-center sm:items-center sm:px-6 sm:py-8 lg:px-10">
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            handlePassword(e);
          }} 
          className="relative z-20 flex min-h-dvh w-full max-w-md flex-col items-center gap-5 overflow-hidden bg-white/95 px-5 py-5 backdrop-blur-sm sm:min-h-0 sm:gap-6 sm:rounded-2xl sm:border sm:border-white/20 sm:px-8 sm:py-8 sm:shadow-2xl sm:transition-all sm:duration-500 sm:hover:shadow-3xl lg:max-w-lg"
        >
          {/* Decorative elements */}
          <div className="absolute -left-10 -top-10 z-10 hidden h-20 w-20 rotate-12 rounded-2xl border-2 border-dark-lavender bg-gradient-to-br from-light-lavender to-dark-lavender2 shadow-2xl transition-transform duration-300 sm:block"></div>
          
          {/* Header */}
          <div className="pb-1 pt-2 text-center sm:pt-3">
            <img src={logo} alt="Nexum" className="mx-auto mb-3 h-12 w-auto drop-shadow-md md:hidden" />
            <h1 className="mb-2 font-K2D text-2xl font-bold tracking-wide text-gray-700 sm:text-3xl 3xl:text-4xl">
              RÉINITIALISER
            </h1>
            <div className="w-16 h-1 bg-gradient-to-r from-yellow-trio to-yellow-400 rounded-full mx-auto"></div>
          </div>
          
          {/* Description */}
          <div className="w-full max-w-sm space-y-2 text-center">
            <p className="text-gray-700 text-sm 3xl:text-base font-BarlowCondensed">
              Veuillez renseigner votre nouveau mot de passe.
            </p>
            <p className="font-BarlowCondensed text-xs leading-relaxed text-gray-600 min-[360px]:text-sm 3xl:text-base">
              Celui-ci doit contenir au minimum 8 caractères, au maximum 30, une
              majuscule, une minuscule, un chiffre et un caractère spécial.
            </p>
          </div>
          
          {/* Password field */}
          <div className="w-full max-w-sm space-y-1.5 sm:space-y-2">
            <label
              htmlFor="newPassword"
              className="text-gray-700 text-sm 3xl:text-lg font-BarlowCondensed font-medium block"
            >
              Nouveau mot de passe
            </label>
            <div className="relative group">
              <input
                type={passwordInputType}
                className="form-input-modern h-11 w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 pr-12 text-base text-gray-700 shadow-sm transition-all duration-300 placeholder:text-gray-400 group-hover:shadow-md focus:border-yellow-trio focus:bg-white focus:outline-none sm:h-12"
                pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
                onChange={(e) => setPasswordResetPassword(e.target.value)}
                name="newPassword"
                id="newPassword"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                onClick={(e) => togglePasswordVisibility(e)}
              >
                <img src={Eye} alt="oeil" className="w-5 h-5 opacity-60 hover:opacity-100 transition-opacity duration-200" />
              </button>
              <div className="absolute inset-0 rounded-xl transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-yellow-trio/10 group-hover:to-yellow-400/10 pointer-events-none"></div>
            </div>
          </div>
          
          {/* Verify password field */}
          <div className="w-full max-w-sm space-y-1.5 sm:space-y-2">
            <label
              htmlFor="verifyPassword"
              className="text-gray-700 text-sm 3xl:text-lg font-BarlowCondensed font-medium block"
            >
              Confirmer le mot de passe
            </label>
            <div className="relative group">
              <input
                type={passwordInputType}
                className="form-input-modern h-11 w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 pr-12 text-base text-gray-700 shadow-sm transition-all duration-300 placeholder:text-gray-400 group-hover:shadow-md focus:border-yellow-trio focus:bg-white focus:outline-none sm:h-12"
                pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
                name="verifyPassword"
                id="verifyPassword"
                onChange={(e) => setVerifyPasswordResetPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                onClick={(e) => togglePasswordVisibility(e)}
              >
                <img src={Eye} alt="oeil" className="w-5 h-5 opacity-60 hover:opacity-100 transition-opacity duration-200" />
              </button>
              <div className="absolute inset-0 rounded-xl transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-yellow-trio/10 group-hover:to-yellow-400/10 pointer-events-none"></div>
            </div>
          </div>
          
          {/* Reset button */}
          <button
            type="submit"
            tabIndex={0}
            className="btn-modern mt-1 h-11 w-full max-w-sm cursor-pointer rounded-xl border-0 bg-gradient-to-r from-yellow-trio to-yellow-400 font-BarlowCondensed text-lg font-semibold text-gray-700 shadow-lg transition-all duration-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-yellow-trio focus:ring-offset-2 sm:h-12 sm:w-40 sm:hover:scale-105"
          >
            Réinitialiser
          </button>
          
          {/* Decorative element */}
          <div className="absolute -bottom-10 -right-10 z-10 hidden h-20 w-20 -rotate-12 rounded-2xl border-2 border-light-lavender bg-gradient-to-br from-light-lavender to-dark-lavender shadow-2xl transition-transform duration-300 sm:block"></div>
        </form>
      </main>
    </div>
  );
};

export default ResetPassword;
